import { useCallback, useEffect, useState } from 'react';
import { on, removeListener, run } from '../helper/electron';
import isElectron from '../utils/isElectron';

export const useAppUpdate = () => {
  const [checkSettled, setCheckSettled] = useState(!isElectron());
  const [showUpdate, setShowUpdate] = useState(false);
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [updateReady, setUpdateReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!isElectron()) return;

    on('update-available', (version: string) => {
      setCheckSettled(true);
      setAvailableVersion(version || null);
      setShowUpdate(true);
      setError(null);
    });

    on('update-not-available', () => {
      setCheckSettled(true);
    });

    on('update-progress', (response: string) => {
      setProgress(Math.round(parseFloat(response)));
    });

    on('update-downloaded', () => {
      setUpdateReady(true);
    });

    on('update-error', (message: string) => {
      setCheckSettled(true);
      setInstalling(false);
      setError(message || 'Something went wrong with the update.');
      setShowUpdate(true);
    });

    run('update-check');

    return () => {
      removeListener('update-available');
      removeListener('update-not-available');
      removeListener('update-progress');
      removeListener('update-downloaded');
      removeListener('update-error');
    };
  }, []);

  const resetUpdate = useCallback(() => {
    setShowUpdate(false);
    setAvailableVersion(null);
    setProgress(0);
    setUpdateReady(false);
    setError(null);
    setInstalling(false);
  }, []);

  const onInstall = useCallback(() => {
    setInstalling(true);
    setError(null);
    run('update-install');
  }, []);

  const onRetryCheck = useCallback(() => {
    setError(null);
    setProgress(0);
    setUpdateReady(false);
    setInstalling(false);
    setShowUpdate(false);
    setCheckSettled(false);
    run('update-check');
  }, []);

  return {
    checkSettled,
    showUpdate,
    availableVersion,
    progress,
    updateReady,
    error,
    installing,
    onInstall,
    onRetryCheck,
    resetUpdate,
  };
};
